import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Collapse,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  ShowChart as ShowChartIcon,
  Tune as TuneIcon,
  Notifications as NotificationsIcon,
  ExpandLess,
  ExpandMore,
  Science as ScienceIcon,
  MonitorHeart as MonitorHeartIcon,
  Business as BusinessIcon,
  SmartToy as ArtIcon,
  Settings as SettingsIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import AISidebar from '../company/AISidebar';

const drawerWidth = 260;
const artWidth = 380;

function Layout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [crlOpen, setCrlOpen] = useState(true);
  const [insiderOpen, setInsiderOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [artOpen, setArtOpen] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, userData, signOut, hasModuleAccess, isAdmin } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleUserMenuClose();
    await signOut();
    navigate('/login');
  };

  // Detect if we're on a company detail page to pass context to Art
  const companyMatch = location.pathname.match(/^\/company\/(.+)/);
  const companyId = companyMatch ? companyMatch[1] : null;

  const crlMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/crl' },
    { text: 'Letters', icon: <DescriptionIcon />, path: '/crl/letters' },
    { text: 'Email Alerts', icon: <EmailIcon />, path: '/crl/alerts', adminOnly: true },
  ];

  const insiderMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/insider' },
    { text: 'ML Signals', icon: <ShowChartIcon />, path: '/insider/signals' },
    { text: 'Strategy', icon: <TuneIcon />, path: '/insider/strategy' },
    { text: 'Alert Settings', icon: <NotificationsIcon />, path: '/insider/alerts', adminOnly: true },
  ];

  const drawer = (
    <Box>
      <Toolbar sx={{ px: 2 }}>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
          Analytics
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {/* CRL Module */}
        {hasModuleAccess('crl') && (
          <>
            <ListItemButton onClick={() => setCrlOpen(!crlOpen)}>
              <ListItemIcon>
                <ScienceIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="FDA CRL Analysis"
                primaryTypographyProps={{ fontWeight: 500 }}
              />
              {crlOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={crlOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {crlMenuItems.map((item) => {
                  if (item.adminOnly && !isAdmin()) return null;
                  return (
                    <ListItemButton
                      key={item.path}
                      sx={{ pl: 4 }}
                      selected={location.pathname === item.path}
                      onClick={() => handleNavigation(item.path)}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </>
        )}

        {/* Insider Module */}
        {hasModuleAccess('insider') && (
          <>
            <ListItemButton onClick={() => setInsiderOpen(!insiderOpen)}>
              <ListItemIcon>
                <TrendingUpIcon color="secondary" />
              </ListItemIcon>
              <ListItemText
                primary="Insider Trading"
                primaryTypographyProps={{ fontWeight: 500 }}
              />
              {insiderOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={insiderOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {insiderMenuItems.map((item) => {
                  if (item.adminOnly && !isAdmin()) return null;
                  return (
                    <ListItemButton
                      key={item.path}
                      sx={{ pl: 4 }}
                      selected={location.pathname === item.path}
                      onClick={() => handleNavigation(item.path)}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </>
        )}

        {/* Company Intelligence Module */}
        {hasModuleAccess('company') && (
          <ListItemButton
            selected={location.pathname.startsWith('/company')}
            onClick={() => handleNavigation('/company')}
          >
            <ListItemIcon>
              <BusinessIcon sx={{ color: '#00897b' }} />
            </ListItemIcon>
            <ListItemText
              primary="Company Intelligence"
              primaryTypographyProps={{ fontWeight: 500 }}
            />
          </ListItemButton>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Admin Section */}
        {isAdmin() && (
          <>
            <ListItemButton
              selected={location.pathname === '/admin/users'}
              onClick={() => handleNavigation('/admin/users')}
            >
              <ListItemIcon>
                <PeopleIcon />
              </ListItemIcon>
              <ListItemText primary="User Management" />
            </ListItemButton>
            <ListItemButton
              selected={location.pathname === '/admin/pipeline-status'}
              onClick={() => handleNavigation('/admin/pipeline-status')}
            >
              <ListItemIcon>
                <MonitorHeartIcon />
              </ListItemIcon>
              <ListItemText primary="Pipeline Status" />
            </ListItemButton>
            <ListItemButton
              selected={location.pathname === '/admin/agent-settings'}
              onClick={() => handleNavigation('/admin/agent-settings')}
            >
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Agent Settings" />
            </ListItemButton>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px${artOpen ? ` - ${artWidth}px` : ''})` },
          ml: { md: `${drawerWidth}px` },
          mr: { md: artOpen ? `${artWidth}px` : 0 },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {location.pathname.startsWith('/crl') ? 'FDA CRL Analysis' :
             location.pathname.startsWith('/insider') ? 'Insider Trading Monitor' :
             location.pathname.startsWith('/company') ? 'Company Intelligence' :
             location.pathname.startsWith('/admin') ? 'Administration' : 'Dashboard'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.displayName || user?.email}
            </Typography>
            <IconButton onClick={handleUserMenuOpen} sx={{ p: 0 }}>
              <Avatar
                alt={user?.displayName}
                src={user?.photoURL}
                sx={{ width: 36, height: 36 }}
              />
            </IconButton>
            {/* Art toggle button */}
            <Tooltip title={artOpen ? 'Close Art' : 'Ask Art — Biotech Intelligence Agent'}>
              <IconButton
                color="inherit"
                onClick={() => setArtOpen(!artOpen)}
                sx={{
                  ml: 1,
                  bgcolor: artOpen ? 'rgba(255,255,255,0.15)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                }}
              >
                <Badge
                  variant="dot"
                  color="success"
                  invisible={artOpen}
                >
                  <ArtIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {userData?.role === 'admin' ? 'Administrator' : 'Viewer'}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Left nav drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px${artOpen ? ` - ${artWidth}px` : ''})` },
          minHeight: '100vh',
          bgcolor: 'background.default',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      {/* Art — right sidebar */}
      <Drawer
        variant="persistent"
        anchor="right"
        open={artOpen}
        sx={{
          width: artOpen ? artWidth : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: artWidth,
            boxSizing: 'border-box',
            top: 0,
            height: '100%',
            borderLeft: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <AISidebar
          companyId={companyId}
          companyName={companyId ? undefined : undefined}
        />
      </Drawer>
    </Box>
  );
}

export default Layout;
